import type { Game } from "@rally/rating";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { correctMatch, formatScoreline, sideNames } from "../lib/matches.js";
import { FORMATS } from "../lib/sport.js";
import type { MatchFormat, MatchSummary } from "../lib/types.js";
import { ScorePad } from "./ScorePad.js";
import { Button, inputStyle, Notice } from "./ui.js";

/**
 * Correcting a score that was typed wrong.
 *
 * The remedy for this used to be `void_match`, which says the match never happened — a lie
 * about an evening two people spent on court, and a hole in both their records. This changes
 * the score and leaves everything else: the match, its place in the log, and who agreed to
 * play it.
 *
 * **The reason is required**, and it is not paperwork. Somebody's rating is about to move
 * because an organiser said the score was different from what everybody confirmed, and the
 * audit entry — old score, new score, who, why — is the only thing that makes that
 * accountable rather than merely allowed.
 *
 * Ratings are dropped rather than adjusted, so the numbers are wrong until somebody runs
 * `npm run recompute`. The screen says so instead of hiding it.
 */
export function FixScore({
  match,
  onCancel,
  onDone,
}: {
  match: MatchSummary;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [format, setFormat] = useState<MatchFormat>(FORMATS[0]!);
  const [games, setGames] = useState<Game[]>([]);
  const [reason, setReason] = useState("");
  const [reviewing, setReviewing] = useState(false);

  const submit = useMutation({
    mutationFn: () => correctMatch(match.id, games, reason),
    onSuccess: onDone,
  });

  const onGamesChange = useCallback((next: Game[]) => setGames(next), []);
  const nameA = sideNames(match, "A");
  const nameB = sideNames(match, "B");
  const ready = reason.trim().length >= 3;

  return (
    <div className="mt-3 border-t pt-3" style={{ borderColor: "var(--color-line-muted)" }}>
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <span className="micro">
          Was {formatScoreline(match.games)} · {match.winner === "A" ? nameA : nameB} won
        </span>
        <button
          type="button"
          onClick={onCancel}
          className="micro underline"
          style={{ minHeight: 44 }}
        >
          Cancel
        </button>
      </div>

      {reviewing ? (
        <>
          <div className="font-mono text-lg">{formatScoreline(games)}</div>
          <div className="micro mt-1">{winnerName(games, nameA, nameB)} won</div>

          <label className="mt-4 block">
            <span className="field-label">Why it is being changed</span>
            <input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Typed 21-9 instead of 21-19"
              maxLength={140}
              style={inputStyle}
            />
          </label>

          <Notice tone="attention">
            {match.status === "confirmed"
              ? "This drops the ratings this match produced. Run the recompute afterwards, or the board will be a little out until somebody does."
              : "Nothing has been rated yet, so this only changes the score."}
          </Notice>

          <div className="flex flex-wrap gap-3">
            <Button
              tone="primary"
              onClick={() => submit.mutate()}
              disabled={!ready || submit.isPending}
            >
              {submit.isPending ? "Saving…" : "Save the correction"}
            </Button>
            <Button onClick={() => setReviewing(false)} disabled={submit.isPending}>
              Back to the score
            </Button>
          </div>

          {!ready ? (
            <p className="micro mt-2">A reason is required — it goes in the audit trail.</p>
          ) : null}
          {submit.error ? <Notice tone="attention">{submit.error.message}</Notice> : null}
        </>
      ) : (
        <ScorePad
          nameA={nameA}
          nameB={nameB}
          format={format}
          onGamesChange={onGamesChange}
          onDone={() => setReviewing(true)}
        />
      )}

      {!reviewing ? (
        <button
          type="button"
          onClick={() => setFormat(FORMATS[(FORMATS.indexOf(format) + 1) % FORMATS.length]!)}
          className="micro mt-3 underline"
          style={{ minHeight: 44 }}
        >
          Format: {format.bestOf} × {format.target}
        </button>
      ) : null}
    </div>
  );
}

function winnerName(games: Game[], nameA: string, nameB: string): string {
  let a = 0;
  let b = 0;
  for (const game of games) {
    if (game.a > game.b) a++;
    else b++;
  }
  return a > b ? nameA : nameB;
}
